"use client";

import { useState } from "react";
import GameLibrary from "@/components/GameLibrary";
import { GameCard } from "@/components/GameCard";
import { GameItem, SequenceSetupData } from "@/app/types";
import { FindTheMatch } from '@/assets/icons/gameicon';
import SequenceSetupModal from "@/components/ui/SequenceSetupModal";
import SequenceBuilder from "@/components/pages/SequenceBuilder";

const sequenceGame: GameItem = {
  id: "seq-1",
  title: "Sequence",
  description: "Tap the matching answer to eliminate it. Repeat until all answers are gone.",
  icon: <FindTheMatch />,
};

const EMPTY_SEQUENCE_SETUP: SequenceSetupData = {
  title: "",
  subject: "None",
  topic: "None",
  grade: "None",
  backgroundImage: null,
};

type SequenceEntry = {
  id: string;
  setup: SequenceSetupData;
};

export default function CreateGame() {
  const [activeTab, setActiveTab] = useState<"game" | "sequence">("game");
  const [isSequenceModalOpen, setIsSequenceModalOpen] = useState(false);
  const [sequenceDraft, setSequenceDraft] = useState<SequenceSetupData>(EMPTY_SEQUENCE_SETUP);
  const [sequenceFileName, setSequenceFileName] = useState("");
  const [sequenceItems, setSequenceItems] = useState<SequenceEntry[]>([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState<string | null>(null);

  const selectedSequence = sequenceItems.find((item) => item.id === selectedSequenceId);

  const handleOpenSequenceModal = () => {
    setSequenceDraft(EMPTY_SEQUENCE_SETUP);
    setSequenceFileName("");
    setIsSequenceModalOpen(true);
  };

  const handleConfirmSequence = () => {
    const title = sequenceDraft.title.trim();
    if (!title) return;

    const entry: SequenceEntry = {
      id: `sequence-${Date.now()}`,
      setup: {
        ...sequenceDraft,
        title,
      },
    };

    setSequenceItems((prev) => [...prev, entry]);
    setIsSequenceModalOpen(false);
    setSelectedSequenceId(entry.id);
  };

  const handleOpenSequenceBuilder = (id: string) => {
    setSelectedSequenceId(id);
  };

  const handleBackToSequenceList = () => {
    setSelectedSequenceId(null);
  };

  return (
    <div className="flex h-full w-full flex-col font-sans">
      {!selectedSequence && (
        <div className="flex -mb-px z-10 w-full">
          <button
            onClick={() => setActiveTab("game")}
            className={`px-8 py-3 text-sm font-bold rounded-t-lg transition-colors border-b-0 ${activeTab === "game"
              ? "bg-[#FDEF5C] text-[#2B2B2B]"
              : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
          >
            Create Game
          </button>
          <button
            onClick={() => setActiveTab("sequence")}
            className={`px-8 py-3 text-sm font-bold rounded-t-lg transition-colors ml-1 border-b-0 ${activeTab === "sequence"
              ? "bg-[#FDEF5C] text-[#2B2B2B]"
              : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
          >
            Create Sequence
          </button>
        </div>
      )}

      <div className="flex-1 w-full flex justify-center h-full">
        {activeTab === "game" && (
          <div className="w-full">
            <GameLibrary />
          </div>
        )}
        {activeTab === "sequence" && (
          <>
            {!selectedSequence && (
              <div className="w-full h-full bg-white rounded-2xl flex flex-col p-6 sm:p-9 shadow-sm">
                <div className="max-w-md">
                  <GameCard
                    item={sequenceGame}
                    onClick={handleOpenSequenceModal}
                  />
                </div>

                {sequenceItems.length > 0 && (
                  <div className="mt-6 w-full max-w-md">
                    <div className="mb-3">
                      <div className="text-lg font-bold text-[#4b4b4b]">Sequence</div>
                    </div>
                    <div className="space-y-3">
                      {sequenceItems.map((sequence) => (
                        <button
                          key={sequence.id}
                          type="button"
                          onClick={() => handleOpenSequenceBuilder(sequence.id)}
                          className="flex h-[74px] w-full items-center justify-center rounded-xl border-2 border-[#0092D7] bg-[#f5f5f5] px-4 text-3xl font-bold text-[#565656] transition-colors hover:bg-[#efefef]"
                        >
                          {sequence.setup.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedSequence && (
              <SequenceBuilder
                sequenceName={selectedSequence.setup.title}
                onBack={handleBackToSequenceList}
              />
            )}

            <SequenceSetupModal
              open={isSequenceModalOpen}
              value={sequenceDraft}
              fileName={sequenceFileName}
              onFileNameChange={setSequenceFileName}
              onChange={setSequenceDraft}
              onCancel={() => setIsSequenceModalOpen(false)}
              onConfirm={handleConfirmSequence}
            />
          </>
        )}
      </div>
    </div>
  );
}
